/**
 * Bench API (owner: C5): batches and guided weighing, dilutions, evaluations, reminders, ladders and the
 * lookups the bench screens use, under /api/bench/ (backend/bench/api.py). Balance, printer and QR
 * drivers live in src/devices/. Query keys start with "bench".
 */
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/api/client";
import type { DecimalString, IsoDate, IsoDateTime, ListParams, Paged, Uuid } from "@/api/types";

// --- shared ----------------------------------------------------------------------------------------

export type BatchPurpose = "dilution" | "trial" | "production";
export type BatchStatus = "planned" | "weighing" | "done" | "cancelled";
export type LineStatus = "pending" | "in_tolerance" | "under" | "over" | "accepted";
export type Severity = "info" | "warning" | "error";

export interface PlanWarning {
  code: string;
  message: string;
  material_id: string | null;
  severity: Severity;
}

export interface BenchSettings {
  resolution_g: DecimalString;
  rel_tol: DecimalString;
  abs_tol_g: DecimalString;
  k: DecimalString;
  min_weigh_g: DecimalString;
  auto_advance_ms: number;
  resmell_days: number[];
  maceration_days: number;
}

export interface MaterialRef {
  id: Uuid;
  name: string;
  kind: string;
  is_solvent: boolean;
  short_code: string | null;
}

export interface CarriedSolvent {
  material_id: string;
  name: string;
  grams: DecimalString;
}

export interface PlanSummary {
  batch_mass_g: DecimalString;
  concentration: DecimalString;
  concentrate_g: DecimalString;
  solvent_g: DecimalString;
  min_weigh_g: DecimalString;
  rescale_factor: DecimalString;
  carried: CarriedSolvent[];
}

// --- planning --------------------------------------------------------------------------------------

export interface BatchRequest {
  version_id: Uuid;
  target_mass_g: DecimalString;
  concentration: DecimalString;
  purpose: BatchPurpose;
  solvent_material_id?: Uuid | null;
  solvent_lot_id?: Uuid | null;
  /** Material id -> lot id, or "neat" for an unrecorded neat bottle. */
  choices?: Record<string, string>;
  maceration_days?: number | null;
  notes?: string;
}

export interface FormOption {
  lot_id: Uuid;
  lot_code: string;
  label: string;
  active_fraction: DecimalString;
  carrier_name: string | null;
  balance_g: DecimalString;
  weigh_g: DecimalString;
  weighable: boolean;
}

export interface PlanLine {
  plan_key: string;
  kind: "line" | "carrier" | "solvent";
  material_id: string;
  material_name: string;
  lot_id: Uuid | null;
  lot_code: string | null;
  lot_balance_g: DecimalString | null;
  active_fraction: DecimalString;
  carrier_id: string | null;
  carrier_name: string | null;
  form_label: string;
  neat_g: DecimalString;
  weigh_g: DecimalString;
  carried_g: DecimalString;
  tolerance_g: DecimalString;
  below_minimum: boolean;
  choice: string | null;
  options: FormOption[];
}

export interface PlanPreview {
  summary: PlanSummary;
  lines: PlanLine[];
  warnings: PlanWarning[];
  has_errors: boolean;
  solvent: MaterialRef | null;
  settings: BenchSettings;
}

export interface DilutionRequest {
  lot_id: Uuid;
  target_active_fraction: DecimalString;
  total_mass_g: DecimalString;
  solvent_material_id?: Uuid | null;
  solvent_lot_id?: Uuid | null;
  notes?: string;
}

// --- batches ---------------------------------------------------------------------------------------

export interface BatchSummary {
  id: Uuid;
  code: string;
  purpose: BatchPurpose;
  status: BatchStatus;
  title: string;
  formula_version_id: Uuid | null;
  formula_name: string | null;
  version_label: string | null;
  target_mass_g: DecimalString;
  concentration_fraction: DecimalString;
  blind_code: string | null;
  ladder_id: Uuid | null;
  output_lot_id: Uuid | null;
  output_lot_code: string | null;
  macerate_until: IsoDate | null;
  lines_done: number;
  lines_total: number;
  created_at: IsoDateTime;
  started_at: IsoDateTime | null;
  finished_at: IsoDateTime | null;
}

export interface BatchLine {
  id: Uuid;
  position: number;
  kind: "line" | "solvent" | "addition";
  plan_key: string;
  material_id: Uuid;
  material_name: string;
  input_lot_id: Uuid | null;
  input_lot_code: string | null;
  active_fraction: DecimalString;
  carrier_material_id: Uuid | null;
  carrier_name: string | null;
  form_label: string;
  planned_g: DecimalString;
  neat_g: DecimalString | null;
  carried_g: DecimalString | null;
  tolerance_g: DecimalString | null;
  actual_g: DecimalString | null;
  deviation_fraction: DecimalString | null;
  status: LineStatus;
  topup_g: DecimalString | null;
  below_minimum: boolean;
  raw_reading: string;
  balance_reading_g: DecimalString | null;
  stable: boolean;
  action: "auto" | "manual" | "override" | null;
  recorded_at: IsoDateTime | null;
  notes: string;
}

export interface BatchEvent {
  kind: "rescale" | "accept";
  at: IsoDateTime;
  factor?: DecimalString;
  rescale_factor?: DecimalString;
  batch_mass_g?: DecimalString;
}

export interface Batch extends BatchSummary {
  formula_id: Uuid | null;
  formula_code: string | null;
  solvent_material_id: Uuid | null;
  solvent_name: string | null;
  balance_model: string;
  balance_serial: string;
  balance_resolution_g: DecimalString | null;
  rescale_factor: DecimalString;
  maceration_days: number | null;
  derived_version_id: Uuid | null;
  derived_version_label: string | null;
  operator_name: string | null;
  notes: string;
  settings: BenchSettings;
  plan: PlanSummary | null;
  warnings: PlanWarning[];
  events: BatchEvent[];
  lines: BatchLine[];
  progress: { done: number; total: number };
  vessel: { total_g: DecimalString; concentrate_g: DecimalString; concentration: DecimalString } | null;
}

export interface RecordRequest {
  actual_g: DecimalString;
  raw_reading?: string;
  stable?: boolean;
  action: "auto" | "manual" | "override";
  balance_reading_g?: DecimalString | null;
}

export interface RescaleResult {
  factor: DecimalString;
  rescale_factor: DecimalString;
  batch_mass_g: DecimalString;
  lines: Array<{
    line_id: Uuid;
    planned_g: DecimalString;
    tolerance_g: DecimalString | null;
    topup_g: DecimalString | null;
  }>;
  accept: {
    total_g: DecimalString;
    concentrate_g: DecimalString;
    concentration: DecimalString;
    shares: Array<{
      material_id: string;
      material_name: string;
      planned: DecimalString;
      accepted: DecimalString;
      deviation: DecimalString;
    }>;
  } | null;
  warnings: PlanWarning[];
  batch: Batch;
}

export interface AdditionRequest {
  material_id: Uuid;
  lot_id?: Uuid | null;
  grams: DecimalString;
  active_fraction?: DecimalString;
  carrier_material_id?: Uuid | null;
}

export interface CompleteRequest {
  maceration_days?: number | null;
  resmell_days?: number[] | null;
  location_id?: Uuid | null;
  notes?: string | null;
}

export interface BatchListParams extends ListParams {
  status?: BatchStatus | "active";
  purpose?: BatchPurpose;
  ladder?: Uuid;
  version?: Uuid;
}

// --- evaluations, reminders, ladders ---------------------------------------------------------------

export type Medium = "strip" | "skin" | "air" | "cloth";
export type EvaluationSubjectKind = "raw_material" | "trial" | "ladder_sample" | "production" | "dilution";

export interface Evaluation {
  id: Uuid;
  lot_id: Uuid | null;
  lot_code: string | null;
  batch_id: Uuid | null;
  batch_code: string | null;
  material_id: Uuid | null;
  material_name: string | null;
  subject: string;
  subject_kind: EvaluationSubjectKind;
  is_journal: boolean;
  timepoint_label: string;
  minutes_after: number | null;
  medium: Medium;
  blind_code: string;
  rating: number | null;
  intensity: number | null;
  longevity_hours: DecimalString | null;
  descriptor_ids: string[];
  descriptors: Array<{ id: Uuid; name: string }>;
  notes: string;
  evaluated_at: IsoDateTime;
  created_at: IsoDateTime;
}

export interface EvaluationRequest {
  lot_id?: Uuid | null;
  batch_id?: Uuid | null;
  material_id?: Uuid | null;
  subject_kind?: EvaluationSubjectKind;
  timepoint_label?: string;
  minutes_after?: number | null;
  medium?: Medium;
  blind_code?: string;
  rating?: number | null;
  intensity?: number | null;
  longevity_hours?: DecimalString | null;
  descriptor_ids?: Uuid[];
  notes?: string;
}

export interface EvaluationListParams extends ListParams {
  lot?: Uuid;
  batch?: Uuid;
  material?: Uuid;
  subject_kind?: EvaluationSubjectKind;
  journal?: boolean;
}

export type ReminderKind = "resmell" | "shelf_life" | "retest" | "reorder" | "custom";
export type ReminderStatus = "open" | "done" | "snoozed" | "dismissed";

export interface Reminder {
  id: Uuid;
  kind: ReminderKind;
  title: string;
  status: ReminderStatus;
  due_at: IsoDateTime;
  is_due: boolean;
  subject_type: string;
  subject_id: string;
  subject_url: string | null;
  rule: string;
  snoozed_until: IsoDateTime | null;
  completed_at: IsoDateTime | null;
  notes: string;
  created_at: IsoDateTime;
}

export interface ReminderListParams extends ListParams {
  status?: "open" | "due" | "upcoming" | "done" | "dismissed" | "snoozed" | "all";
  kind?: ReminderKind;
  days?: number;
}

export interface ReminderPatch {
  status?: ReminderStatus;
  snooze_days?: number;
  due_on?: IsoDate;
  title?: string;
  notes?: string;
}

export interface LadderStep {
  blind_code: string;
  batch_id: Uuid | null;
  batch_status: BatchStatus | null;
  batch_code: string | null;
  index: number | null;
  label: string | null;
  value_pct: DecimalString | null;
  version_id: Uuid | null;
  version_label: string | null;
}

export interface Ladder {
  id: Uuid;
  name: string;
  mode: "dose" | "add";
  blind: boolean;
  revealed: boolean;
  base_version_id: Uuid;
  base_label: string;
  material_id: Uuid | null;
  material_name: string | null;
  notes: string;
  created_at: IsoDateTime;
  steps: LadderStep[];
  batches_done: number;
}

export interface LadderRequest {
  base_version_id: Uuid;
  mode: "dose" | "add";
  material_id?: Uuid | null;
  steps?: DecimalString[];
  candidates?: Array<{ material_id: Uuid; pct: DecimalString }>;
  blind: boolean;
  name?: string;
  notes?: string;
  target_mass_g: DecimalString;
  concentration: DecimalString;
  solvent_material_id?: Uuid | null;
}

export interface CompareStep extends LadderStep {
  output_lot_id: Uuid | null;
  output_lot_code: string | null;
  count: number;
  mean_rating: DecimalString | null;
  mean_intensity: DecimalString | null;
  descriptors: Array<{ id: Uuid; name: string; count: number }>;
  evaluations: Evaluation[];
}

export interface LadderComparison {
  ladder: Ladder;
  revealed: boolean;
  steps: CompareStep[];
}

// --- lookups ---------------------------------------------------------------------------------------

export interface VersionRef {
  id: Uuid;
  formula_id: Uuid;
  formula_code: string;
  formula_name: string;
  formula_kind: string;
  label: string;
  status: string;
  created_at: IsoDateTime;
}

export interface LotRef {
  id: Uuid;
  code: string;
  material_id: Uuid;
  material_name: string;
  active_fraction: DecimalString;
  carrier_name: string | null;
  balance_g: DecimalString | null;
  status: string;
  origin: string;
  is_raw_material: boolean;
  subject_kind: EvaluationSubjectKind;
}

export interface OdorTermRef {
  id: Uuid;
  name: string;
  kind: string;
  parent_id: Uuid | null;
  family: string | null;
  family_id: string | null;
}

export interface LocationRef {
  id: Uuid;
  code: string;
  name: string;
  kind: string;
}

// --- query keys and queries --------------------------------------------------------------------------

export const benchKeys = {
  all: ["bench"] as const,
  settings: ["bench", "settings"] as const,
  batches: (params: BatchListParams) => ["bench", "batches", params] as const,
  batch: (id: Uuid) => ["bench", "batch", id] as const,
  preview: (body: BatchRequest | null) => ["bench", "preview", body] as const,
  dilutionPreview: (body: DilutionRequest | null) => ["bench", "dilution-preview", body] as const,
  evaluations: (params: EvaluationListParams) => ["bench", "evaluations", params] as const,
  reminders: (params: ReminderListParams) => ["bench", "reminders", params] as const,
  ladders: (params: ListParams) => ["bench", "ladders", params] as const,
  ladder: (id: Uuid, reveal: boolean) => ["bench", "ladder", id, reveal] as const,
  compare: (id: Uuid, reveal: boolean) => ["bench", "compare", id, reveal] as const,
  odorTerms: ["bench", "odor-terms"] as const,
  solvents: ["bench", "solvents"] as const,
  version: (id: Uuid) => ["bench", "version", id] as const,
};

export const benchSettingsQuery = queryOptions({
  queryKey: benchKeys.settings,
  queryFn: ({ signal }) => apiGet<BenchSettings>("/bench/settings", { signal }),
  staleTime: 60_000,
});

export function useBatches(params: BatchListParams) {
  return useQuery({
    queryKey: benchKeys.batches(params),
    queryFn: ({ signal }) => apiGet<Paged<BatchSummary>>("/bench/batches", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function batchQuery(id: Uuid) {
  return queryOptions({
    queryKey: benchKeys.batch(id),
    queryFn: ({ signal }) => apiGet<Batch>(`/bench/batches/${id}`, { signal }),
  });
}

export function usePlanPreview(body: BatchRequest | null) {
  return useQuery({
    queryKey: benchKeys.preview(body),
    queryFn: ({ signal }) => apiPost<PlanPreview>("/bench/plan-preview", body, { signal }),
    enabled: body !== null,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useDilutionPreview(body: DilutionRequest | null) {
  return useQuery({
    queryKey: benchKeys.dilutionPreview(body),
    queryFn: ({ signal }) => apiPost<PlanPreview>("/bench/dilutions/preview", body, { signal }),
    enabled: body !== null,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** After a batch write: put the fresh batch in the cache and refresh lists. */
function useBatchWrite<TBody>(id: Uuid, send: (body: TBody) => Promise<Batch>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: (batch) => queryClient.setQueryData(benchKeys.batch(id), batch),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench", "batches"] }),
  });
}

export function useCreateBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchRequest) => apiPost<Batch>("/bench/batches", body),
    onSuccess: (batch) => {
      queryClient.setQueryData(benchKeys.batch(batch.id), batch);
      void queryClient.invalidateQueries({ queryKey: ["bench", "batches"] });
    },
  });
}

export function useCreateDilution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DilutionRequest) => apiPost<Batch>("/bench/dilutions", body),
    onSuccess: (batch) => {
      queryClient.setQueryData(benchKeys.batch(batch.id), batch);
      void queryClient.invalidateQueries({ queryKey: ["bench", "batches"] });
    },
  });
}

export function useStartBatch(id: Uuid) {
  return useBatchWrite(
    id,
    (body: { balance_model?: string; balance_serial?: string; balance_resolution_g?: DecimalString | null }) =>
      apiPost<Batch>(`/bench/batches/${id}/start`, body),
  );
}

export function useRecordLine(id: Uuid) {
  return useBatchWrite(id, ({ lineId, ...body }: RecordRequest & { lineId: Uuid }) =>
    apiPost<Batch>(`/bench/batches/${id}/lines/${lineId}/record`, body),
  );
}

export function useClearLine(id: Uuid) {
  return useBatchWrite(id, (lineId: Uuid) => apiDelete<Batch>(`/bench/batches/${id}/lines/${lineId}/record`));
}

export function useRescale(id: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mode: "preview" | "rescale" | "accept") =>
      apiPost<RescaleResult>(`/bench/batches/${id}/rescale`, { mode }),
    onSuccess: (result) => queryClient.setQueryData(benchKeys.batch(id), result.batch),
  });
}

export function useAdditions(id: Uuid) {
  return useBatchWrite(id, (body: { additions: AdditionRequest[]; notes?: string }) =>
    apiPost<Batch>(`/bench/batches/${id}/additions`, body),
  );
}

export function useCompleteBatch(id: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CompleteRequest) => apiPost<Batch>(`/bench/batches/${id}/complete`, body),
    onSuccess: (batch) => queryClient.setQueryData(benchKeys.batch(id), batch),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench"] }),
  });
}

export function useCancelBatch(id: Uuid) {
  return useBatchWrite(id, (reason: string) => apiPost<Batch>(`/bench/batches/${id}/cancel`, { reason }));
}

// Evaluations ---------------------------------------------------------------------------------------

export function useEvaluations(params: EvaluationListParams) {
  return useQuery({
    queryKey: benchKeys.evaluations(params),
    queryFn: ({ signal }) => apiGet<Paged<Evaluation>>("/bench/evaluations", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EvaluationRequest) => apiPost<Evaluation>("/bench/evaluations", body),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bench", "evaluations"] }),
        queryClient.invalidateQueries({ queryKey: ["bench", "compare"] }),
      ]),
  });
}

export function useDeleteEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => apiDelete(`/bench/evaluations/${id}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench", "evaluations"] }),
  });
}

export function useUpdateEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: EvaluationRequest & { id: Uuid }) =>
      apiPatch<Evaluation>(`/bench/evaluations/${id}`, body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench", "evaluations"] }),
  });
}

// Reminders -----------------------------------------------------------------------------------------

export function useReminders(params: ReminderListParams) {
  return useQuery({
    queryKey: benchKeys.reminders(params),
    queryFn: ({ signal }) => apiGet<Paged<Reminder>>("/bench/reminders", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

/** Optimistic: the reminder leaves the open lists at once, and comes back if the server says no. */
export function useUpdateReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ReminderPatch & { id: Uuid }) => apiPatch<Reminder>(`/bench/reminders/${id}`, body),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["bench", "reminders"] });
      const snapshot = queryClient.getQueriesData<Paged<Reminder>>({ queryKey: ["bench", "reminders"] });
      if (status && status !== "open") {
        queryClient.setQueriesData<Paged<Reminder>>({ queryKey: ["bench", "reminders"] }, (page) =>
          page
            ? { ...page, items: page.items.filter((item) => item.id !== id), count: Math.max(0, page.count - 1) }
            : page,
        );
      }
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      for (const [key, data] of context?.snapshot ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench", "reminders"] }),
  });
}

export function useCreateReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; due_on: IsoDate; notes?: string }) =>
      apiPost<Reminder>("/bench/reminders", body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench", "reminders"] }),
  });
}

export function useRunReminders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ created: number; dismissed: number; reopened: number }>("/bench/reminders/generate"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench", "reminders"] }),
  });
}

// Ladders -------------------------------------------------------------------------------------------

export function useLadders(params: ListParams) {
  return useQuery({
    queryKey: benchKeys.ladders(params),
    queryFn: ({ signal }) => apiGet<Paged<Ladder>>("/bench/ladders", { params: { ...params }, signal }),
  });
}

export function useLadderComparison(id: Uuid | null, reveal: boolean) {
  return useQuery({
    queryKey: benchKeys.compare(id ?? "", reveal),
    queryFn: ({ signal }) => apiGet<LadderComparison>(`/bench/ladders/${id}/compare`, { params: { reveal }, signal }),
    enabled: id !== null,
  });
}

export function useCreateLadder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LadderRequest) => apiPost<Ladder>("/bench/ladders", body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["bench"] }),
  });
}

// Lookups -------------------------------------------------------------------------------------------

export function searchVersions(q: string, signal: AbortSignal): Promise<VersionRef[]> {
  return apiGet<VersionRef[]>("/bench/lookups/versions", { params: { q }, signal });
}

export function searchMaterials(q: string, signal: AbortSignal): Promise<MaterialRef[]> {
  return apiGet<MaterialRef[]>("/bench/lookups/materials", { params: { q }, signal });
}

export function searchLots(
  q: string,
  signal: AbortSignal,
  material?: Uuid,
  subjectKind?: EvaluationSubjectKind,
): Promise<LotRef[]> {
  return apiGet<LotRef[]>("/bench/lookups/lots", {
    params: { q, material, subject_kind: subjectKind },
    signal,
  });
}

export function searchLocations(q: string, signal: AbortSignal): Promise<LocationRef[]> {
  return apiGet<LocationRef[]>("/bench/lookups/locations", { params: { q }, signal });
}

export function useLot(id: Uuid | null) {
  return useQuery({
    queryKey: ["bench", "lot", id],
    queryFn: async ({ signal }) =>
      (await apiGet<LotRef[]>("/bench/lookups/lots", { params: { id }, signal }))[0] ?? null,
    enabled: id !== null,
  });
}

export function useLots(material: Uuid | null) {
  return useQuery({
    queryKey: ["bench", "lots", material],
    queryFn: ({ signal }) => searchLots("", signal, material ?? undefined),
    enabled: material !== null,
  });
}

export function useVersion(id: Uuid | null) {
  return useQuery({
    queryKey: benchKeys.version(id ?? ""),
    queryFn: async ({ signal }) => {
      const versions = await apiGet<VersionRef[]>("/bench/lookups/versions", { params: { id }, signal });
      return versions[0] ?? null;
    },
    enabled: id !== null,
    staleTime: 60_000,
  });
}

export const odorTermsQuery = queryOptions({
  queryKey: benchKeys.odorTerms,
  queryFn: ({ signal }) => apiGet<OdorTermRef[]>("/bench/lookups/odor-terms", { signal }),
  staleTime: 10 * 60_000,
  retry: false,
});

export const solventsQuery = queryOptions({
  queryKey: benchKeys.solvents,
  queryFn: ({ signal }) => apiGet<MaterialRef[]>("/bench/lookups/solvents", { signal }),
  staleTime: 5 * 60_000,
});
